import axios from '../../../axios.conf';
import { toast as globalToast } from '../../../components/ToastManager';
import { handleExecutionLog } from '../../../components/ExecutionLogHandler';
import {
  FETCH_START,
  FETCH_END,
  MATERIAL_MASTER_LIST_SUCCESS,
  MATERIAL_MASTER_LIST_FAIL,
  MATERIAL_MASTER_SINGLE_DATA_SUCCESS,
  MATERIAL_MASTER_SINGLE_DATA_FAIL,
  EDIT_MATERIAL_MASTER_SUCCESS,
  EDIT_MATERIAL_MASTER_FAIL,
  EDIT_FILTER_CONFIGURATION_SUCCESS,
  EDIT_FILTER_CONFIGURATION_FAIL,
  MATERIAL_MASTER_DESIGN_SUCCESS,
  MATERIAL_MASTER_DESIGN_FAIL,
  CREATE_MATERIAL_MASTER_SUCCESS,
  CREATE_MATERIAL_MASTER_FAIL,
  DELETE_MATERIAL_MASTER_SUCCESS,
  DELETE_MATERIAL_MASTER_FAIL,
  CLEAR_MATERIAL_DESIGN_VIEW,
} from './ActionTypes';
import secureLocalStorage from 'react-secure-storage';
import { history } from '../../../components/Helpers';
// API base URL
const API_URL = process.env.REACT_APP_API_URL;

// Helper function to handle API errors
const handleApiError = (error, dispatch) => {
  dispatch({ type: FETCH_END });
  const errorMessage = error.response?.data?.message || 'An error occurred';
  globalToast.showError(errorMessage);
  return errorMessage;
};

// Get Material Master List
export const getMaterialMasterList = () => async (dispatch) => {
  dispatch({ type: FETCH_START });
  try {
    const response = await axios.get(`${API_URL}/material-master`);
    dispatch({
      type: MATERIAL_MASTER_LIST_SUCCESS,
      data: response.data,
    });
  } catch (error) {
    const errorMessage = handleApiError(error, dispatch);
    dispatch({
      type: MATERIAL_MASTER_LIST_FAIL,
      error: errorMessage,
    });
  }
};

// Get Material Master Single Data
export const getMaterialMasterData = (id) => async (dispatch) => {
  dispatch({ type: FETCH_START });
  try {
    const response = await axios.get(`${API_URL}/material-master/${id}`);
    dispatch({
      type: MATERIAL_MASTER_SINGLE_DATA_SUCCESS,
      data: response.data,
    });
  } catch (error) {
    const errorMessage = handleApiError(error, dispatch);
    dispatch({
      type: MATERIAL_MASTER_SINGLE_DATA_FAIL,
      error: errorMessage,
    });
  }
};

// Create Material Master Data
export const createMaterialMasterData = (data) => async (dispatch) => {
  dispatch({ type: FETCH_START });
  try {
    const modifiedData = {
      ...data,
      update_details: data.update_details
        ? {
          ...data.update_details,
          view_data_operations:
            data.update_details.view_data_operations?.map((op) => ({
              ...op,
              update_type: 'CREATE',
            })) || [],
        }
        : {},
    };
    const response = await axios.post(
      `${API_URL}/material-master`,
      modifiedData,
    );
    dispatch({
      type: CREATE_MATERIAL_MASTER_SUCCESS,
      data: response.data,
    });
    globalToast.showSuccess('Material master data created successfully');
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error, dispatch);
    dispatch({
      type: CREATE_MATERIAL_MASTER_FAIL,
      error: errorMessage,
    });
    return null;
  }
};

// Update Material Master Data
export const updateMaterialMasterData = (id, data) => async (dispatch) => {
  dispatch({ type: FETCH_START });
  try {
    const modifiedData = {
      ...data,
      update_details: data.update_details
        ? {
          ...data.update_details,
          view_data_operations:
            data.update_details.view_data_operations?.map((op) => ({
              ...op,
              update_type: 'CREATE',
            })) || [],
        }
        : {},
    };
    const response = await axios.put(
      `${API_URL}/material-master/${id}`,
      modifiedData,
    );
    dispatch({
      type: EDIT_MATERIAL_MASTER_SUCCESS,
      data: response.data,
    });
    globalToast.showSuccess('Material master data updated successfully');
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error, dispatch);
    dispatch({
      type: EDIT_MATERIAL_MASTER_FAIL,
      error: errorMessage,
    });
    return null;
  }
};

// Update Material Master Design Data
export const updateMaterialDesign =
  (id, data, type = 'material') =>
    async (dispatch) => {
      dispatch({ type: FETCH_START });
      try {
        const modifiedData = {
          ...data,
          update_details: data.update_details
            ? {
              ...data.update_details,
              view_data_operations:
                data.update_details.view_data_operations?.map((op) => ({
                  ...op,
                  update_type: 'CREATE',
                })) || [],
            }
            : {},
        };
        const endpoint =
          type === 'supplier'
            ? 'supplier-master-design'
            : type === 'customer'
              ? 'customer-master-design'
              : type === 'employee'
                ? 'employee-master-design'
                : 'material-master-design';
        const response = await axios.put(`mdm/${endpoint}`, modifiedData);
        dispatch({
          type: EDIT_MATERIAL_MASTER_SUCCESS,
          data: response.data,
        });
        if (response.data && Array.isArray(response.data.message)) {
          response.data.message.forEach((msgObj) => {
            if (msgObj.message_type === 'success') {
              globalToast.showSuccess(msgObj.message);
            } else if (msgObj.message_type === 'error') {
              globalToast.showError(msgObj.message);
            } else if (msgObj.message_type === 'warning') {
              globalToast.showWarning(msgObj.message);
            } else {
              globalToast.showInfo(msgObj.message);
            }
          });
        } else {
          // Legacy or fallback messages
          const profileName = response.data.response?.profile;
          let successMsg = '';

          if (profileName) {
            successMsg = `Profile '${profileName}' updated successfully`;
          } else {
            successMsg =
              type === 'supplier'
                ? 'Supplier master design updated successfully'
                : type === 'customer'
                  ? 'Customer master design updated successfully'
                  : type === 'employee'
                    ? 'Employee master design updated successfully'
                    : 'Material master design updated successfully';

          }

          globalToast.showSuccess(successMsg);
        }


        return response.data.response;
      } catch (error) {
        const errorMessage = handleApiError(error, dispatch);
        dispatch({
          type: EDIT_MATERIAL_MASTER_FAIL,
          error: errorMessage,
        });
        globalToast.showError(errorMessage);
        return null;
      }
    };

// Delete Material Master Data
export const deleteMaterialMasterData = (id) => async (dispatch) => {
  dispatch({ type: FETCH_START });
  try {
    await axios.delete(`${API_URL}/material-master/${id}`);
    dispatch({
      type: DELETE_MATERIAL_MASTER_SUCCESS,
      id,
    });
    globalToast.showSuccess('Material master data deleted successfully');
    return true;
  } catch (error) {
    const errorMessage = handleApiError(error, dispatch);
    dispatch({
      type: DELETE_MATERIAL_MASTER_FAIL,
      error: errorMessage,
    });
    return false;
  }
};
export const updateFilterConfiguration = (id, data, isValidFilter, application, page) => async (dispatch) => {
  dispatch({ type: FETCH_START });
  try {
    const response = await axios.put(`/filter-configuration/${id}`, data, {
      headers: {
        Authorization: secureLocalStorage.getItem('aTk'),
        'Content-Type': 'application/json',
      },
    });

    dispatch({
      type: EDIT_FILTER_CONFIGURATION_SUCCESS,
      data: response.data,
    });

    // Toast success
    const filterGroup = response?.data?.response?.filter_group || 'Filter Group';
    const logNumber = response?.data?.log_number;
    const navigateAfterSave = () => {
      if (isValidFilter) {
        if (page === 'Predefined Queries') {
          history.push('/query-field', { application: 'Data Insights' });
        }
        else
          history.push('/custom-table-configuration-page');
      }
      else {
        history.push({ pathname: '/global-filter-configuration-page', state: { application } });
      }
    };

    if (response?.data?.response?.filter_group && isValidFilter) {
      const entityLabel = page === 'Predefined Queries' ? 'Query' : 'Table';
      globalToast.showSuccess(`${entityLabel} '${filterGroup}' has been Updated successfully!`);
    }
    else if (logNumber) {
      await handleExecutionLog(dispatch, logNumber);
    }
    else {
      globalToast.showSuccess(`Filter Group '${filterGroup}' has been Updated successfully!`);
    }
    navigateAfterSave();
    return response.data.response;
  } catch (error) {
    const errorMessage = handleApiError(error, dispatch);
    dispatch({
      type: EDIT_FILTER_CONFIGURATION_FAIL,
      error: errorMessage,
    });
    return null;
  }
};
// Get Material Master Design Data
export const getMaterialMasterDesignData = () => async (dispatch) => {
  dispatch({ type: FETCH_START });
  try {
    const response = await axios.get(`${API_URL}/material-master-design`);
    dispatch({
      type: MATERIAL_MASTER_DESIGN_SUCCESS,
      data: response.data,
    });
  } catch (error) {
    const errorMessage = handleApiError(error, dispatch);
    dispatch({
      type: MATERIAL_MASTER_DESIGN_FAIL,
      error: errorMessage,
    });
  }
};

// View Material Master Design Data
export const viewMaterialDesign =
  (id, type = 'material') =>
    async (dispatch) => {
      dispatch({ type: FETCH_START });
      try {
        const endpoint =
          type === 'supplier'
            ? 'supplier-master-design'
            : type === 'customer'
              ? 'customer-master-design'
              : 'material-master-design';
        const response = await axios.get(`${API_URL}/${endpoint}`);
        dispatch({
          type: MATERIAL_MASTER_DESIGN_SUCCESS,
          data: response.data,
        });
        return response.data;
      } catch (error) {
        const errorMessage = handleApiError(error, dispatch);
        dispatch({
          type: MATERIAL_MASTER_DESIGN_FAIL,
          error: errorMessage,
        });
        return null;
      }
    };

// Clear Material Design View
export const clearMaterialDesignView = () => ({
  type: CLEAR_MATERIAL_DESIGN_VIEW,
});
